import { shapes } from '@dicebear/collection'
import { createAvatar } from '@dicebear/core'
import { Badge, Box, darken, IconButton, lighten, rgbToHex, Skeleton } from '@mui/material'
import Avatar from '@mui/material/Avatar'
import { avatarPalette } from 'c1ui/theme/palette'
import { AgenticAiIcon } from 'components/core/icons/AgenticAiIcon'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1Link } from 'components/core/links/C1Link'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { UserAvatarUploadDialog } from 'components/pages/user/details/UserAvatarUpload'
import { SystemUserID } from 'lib/constants/system'
import { useSimpleModal } from 'lib/modals'
import { routes, Route } from 'lib/routes'
import { Pencil, UserX } from 'lucide-react'
import Image from 'next/image'
import { UserAvatarRef } from 'pbts/c1/api/assets/v1/user_avatars'
import { UserType } from 'pbts/c1/api/user/v1/user'
import c1IconLight from 'public/static/c1-icon-light.svg'
import { FC, useState } from 'react'
import { useIntl } from 'react-intl'
import { getUserAvatarApi } from 'redux_toolkit/user-avatar'
import { AVATAR_SIZES, AvatarSize, getAvatarIconSize } from './AvatarSizes'
import { avatarStyles, noUserAvatarStyles } from './styles'

type minUser = {
    displayName: string
    id?: string
    email?: string
    type?: UserType
}

export type UserAvatarProps = {
    user: minUser
    size?: AvatarSize
    readOnly?: boolean
    disableColorization?: boolean
    decorative?: boolean
    editable?: boolean
}

export type colorKey = keyof typeof avatarPalette

export const getUserAvatarColor = (userId: string): string => {
    const colorKeys: colorKey[] = Object.keys(avatarPalette) as colorKey[]

    if (!userId) return avatarPalette.blue

    let hash = 0

    for (let i = 0; i < 10; i++) hash += userId.charCodeAt(i)

    const colorIndex = hash % colorKeys.length

    return avatarPalette[colorKeys[colorIndex]]
}

export const NoUserAvatar: FC<{ size?: AvatarSize }> = ({ size }) => (
    <Avatar sx={size ? sxc(avatarStyles.avatar, avatarStyles[size]) : avatarStyles.avatar}>
        <Box sx={sxc(noUserAvatarStyles.noUserColor, size ? noUserAvatarStyles[size] : {})}>
            <C1Icon icon={UserX} />
        </Box>
    </Avatar>
)

type ShapesAvatarProps = {
    email: string
    size?: AvatarSize
}

export const ShapesAvatar: FC<ShapesAvatarProps> = ({ email, size: _size = 'md' }) => {
    const size = AVATAR_SIZES[_size].width

    const colors = Object.values(avatarPalette)

    const backgroundColor = colors.map((color) => lighten(color, 0.8)).map(rgbToHex).map((color) => color.replace('#', ''))
    const shape1Color = colors.map((color) => lighten(color, 0.4)).map(rgbToHex).map((color) => color.replace('#', ''))
    const shape2Color = colors.map((color) => lighten(color, 0.4)).map(rgbToHex).map((color) => color.replace('#', ''))
    const shape3Color = colors.map((color) => darken(color, 0.3)).map(rgbToHex).map((color) => color.replace('#', ''))

    const avatar = createAvatar(shapes, {
        seed: email,
        size,
        backgroundColor,
        shape1Color,
        shape2Color,
        shape3Color,
    })

    // eslint-disable-next-line @next/next/no-img-element
    return <img
        style={{
            borderRadius: '50%',
            overflow: 'hidden',
        }}
        height={size}
        width={size}
        src={avatar.toDataUri()}
        alt={email}
    />
}

type UserAvatarImageProps = {
    userId: string
    displayName: string
    pixelSize: number
}

const UserAvatarImage: FC<UserAvatarImageProps> = ({ userId, displayName, pixelSize }) => {
    const { data, isLoading } = getUserAvatarApi.useMultiGet(UserAvatarRef.create({ userId }), { actionable: !!userId })

    if (data) {
        return <Image
            src={data.src}
            loader={({ width, src }): string => {
                if (!data.srcSet || !data.srcSet.candidates) {
                    return src
                }

                const keys = Object.keys(data.srcSet?.candidates || {}).map(Number).sort((a, b) => b - a)

                for (const key of keys) {
                    if (key >= width) {
                        return data.srcSet?.candidates[key].src || ''
                    }
                }

                const rv = data.srcSet?.candidates[keys[keys.length - 1]].src || ''
                return rv
            }}
            alt={displayName}
            title={displayName}
            width={pixelSize}
            height={pixelSize}
            style={{
                borderRadius: '50%',
                display: 'block',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
            }}
        />
    }

    if (isLoading) {
        return <Skeleton variant="circular" width={pixelSize} height={pixelSize} />
    }

    return null
}

export const UserAvatar: FC<UserAvatarProps> = ({ user, size = 'md', readOnly = false, disableColorization = false, editable = false, decorative = false }) => {
    const [isHovered, setIsHovered] = useState(false)
    const { open } = useSimpleModal()
    const displayName = user.displayName || user.email || ''
    const id = user.id || ''
    const avatarColor = getUserAvatarColor(id)
    const link: Route = user.type === UserType.AGENT ? routes.settings.agenticAi.agent(user.id || '') : routes.users.detail(user.id || '')
    const pixelSize = getAvatarIconSize(size)
    const intl = useIntl()

    // Check if this is the ConductorOne system user
    const isSystemUser = id === SystemUserID

    // Always try to fetch uploaded avatar
    const { data: avatarData } = getUserAvatarApi.useMultiGet(
        UserAvatarRef.create({ userId: id || '' }),
    )

    const avatar = <Avatar
        alt={displayName}
        sx={sxc(
            avatarStyles.avatar,
            size && avatarStyles[size],
            [!readOnly, avatarStyles.mouseHover],
            [user.type === UserType.AGENT, avatarStyles.agentAvatar],
            [isSystemUser, {
                backgroundColor: 'primary.dark',
                padding: 1,
            }],
            [(user.type !== UserType.AGENT && !disableColorization && !isSystemUser && !avatarData), {
                backgroundColor: lighten(avatarColor, 0.8),
                color: darken(avatarColor, 0.3),
            }],
            [!!avatarData, {
                padding: 0,
                backgroundColor: 'transparent',
            }],
        )}
    >
        {isSystemUser
            ? <Image
                src={c1IconLight.src}
                alt={intl.formatMessage({ id: 'c1_system_user', defaultMessage: 'C1 System user' })}
                height={pixelSize}
                width={pixelSize}
                style={{ display: 'block', height: '100%', width: '100%' }}
                loader={({ src }): string => src}
                unoptimized
            />
            : user.type === UserType.AGENT
                ? <AgenticAiIcon />
                : avatarData
                    ? <UserAvatarImage userId={id} displayName={displayName} pixelSize={pixelSize} />
                    : <ShapesAvatar email={user.email || displayName} size={size} />}
    </Avatar>

    const handleEditClick = (): void => {
        open(UserAvatarUploadDialog, { userId: id })()
    }

    let ariaLabel: string | undefined
    switch (user.type) {
        case UserType.SYSTEM:
            ariaLabel = intl.formatMessage({ id: 'c1_system_user', defaultMessage: 'C1 System user' })
            break
        case UserType.AGENT:
            ariaLabel = intl.formatMessage({ id: 'agent_user', defaultMessage: 'Agent user' })
            break
        default:
            ariaLabel = decorative ? undefined : displayName
    }

    const avatarWithEditBadge = editable ? (
        <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
                <IconButton
                    size="small"
                    aria-label="Edit avatar"
                    onClick={(e): void => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleEditClick()
                    }}
                    sx={{
                        backgroundColor: 'primary.main',
                        width: 24,
                        height: 24,
                        opacity: isHovered ? 1 : 0,
                        transition: 'opacity 0.2s',
                        '&:hover, &:focus': {
                            backgroundColor: 'primary.dark',
                            opacity: 1,
                        },
                    }}
                >
                    <C1Icon icon={Pencil} size="sm" className="text-white" />
                </IconButton>
            }
            onMouseEnter={(): void => setIsHovered(true)}
            onMouseLeave={(): void => setIsHovered(false)}
        >
            {avatar}
        </Badge>
    ) : avatar

    // When decorative, avatar is used inside a parent link - don't add another tabbable link
    return (readOnly || !id || decorative) ? avatarWithEditBadge : <C1Link href={link} aria-label={ariaLabel}>{avatarWithEditBadge}</C1Link>
}
